<script setup lang="ts">
import PackagedRoomsCard from '../PackagedRoomsCard.vue';
import RoomCard from '../RoomCard.vue';
import { useData } from '../../_composables/useData';
import ReserveError from '@components/util/ReserveError.vue';
import type RoomsPackage from '@itinerary/hotel/RoomsPackage';

const emit = defineEmits<{
    research: [];
    'update:intoView': [intoView: boolean];
}>();
const roomsRef = ref<HTMLDivElement>();
const packageSelectStore = usePackageSelectStore();
const bundleModule = useBundleModule();
const { selectedBundlePackage } = moduleToRefs(bundleModule);
const { currentHotelModule, hotelPackage, title } = useData();

const isLoading = computed(() => currentHotelModule.value.isLoadingHotelInfo);

const loadingRoom = computed(
    () => isLoading.value || currentHotelModule.value.isLoadingRooms !== false
);
const selectRoomsPackage = (pkg: RoomsPackage) => {
    currentHotelModule.value.selectRoomsPackage(pkg);
    packageSelectStore.loadPreInvoiceForCurrentPackages();
};
const isOneSelectableRoomSelected = computed(
    () =>
        hotelPackage.value.selectedPricesWithPriceClass?.[0]?.[1]
            ?.onlyThisTypeSelectable
);
const prices = computed(() => {
    if (isOneSelectableRoomSelected.value) {
        return hotelPackage.value.prices.filter(
            p => p.id === hotelPackage.value.selectedPrices![0].id
        );
    } else {
        return hotelPackage.value.prices;
    }
});

const onscroll = () => {
    let value = false;
    if (roomsRef.value?.clientHeight) {
        const currentOffset = window.scrollY + window.innerHeight + 48;
        value =
            currentOffset > roomsRef.value.offsetTop &&
            currentOffset <
                roomsRef.value.offsetTop +
                    roomsRef.value.clientHeight +
                    window.innerHeight;
    }
    emit('update:intoView', value);
};
const scrollToTop = () => {
    nextTick(() => {
        if (isOneSelectableRoomSelected.value && roomsRef.value) {
            const top =
                roomsRef.value.getBoundingClientRect().top +
                window.scrollY -
                48;
            window.scrollTo({ top, behavior: 'smooth' });
        }
    });
};
onMounted(() => window.addEventListener('scroll', onscroll));
onBeforeUnmount(() => window.removeEventListener('scroll', onscroll));
</script>
<template>
    <div ref="roomsRef" class="rooms">
        <mr-typography size="x-large" weight="700" type="h2" class="title">
            اتاق‌های موجود در {{ title }}
        </mr-typography>
        <template v-if="loadingRoom">
            <div class="rooms-loading loading-skeleton" />
            <div class="rooms-loading loading-skeleton" />
            <div class="rooms-loading loading-skeleton" />
        </template>
        <template v-else-if="hotelPackage.roomsPackageList.length">
            <PackagedRoomsCard
                v-for="roomsPackage in hotelPackage.roomsPackageList"
                :key="roomsPackage.id"
                :rooms-package
                :nights="hotelPackage.nights"
                :default-rooms-package="
                    selectedBundlePackage?.defaultRoomsPackage
                "
                :is-selected="
                    currentHotelModule.currentSelectedPackageId ===
                    roomsPackage.id
                "
                @select="selectRoomsPackage(roomsPackage)"
            />
        </template>
        <template v-else-if="prices.length">
            <TransitionGroup>
                <RoomCard
                    v-for="price in prices"
                    :key="price.id"
                    :item="price"
                    :nights="hotelPackage.nights"
                    @select="scrollToTop"
                />
                <mr-flash-message
                    v-if="isOneSelectableRoomSelected"
                    message="برای این هتل، تنها رزرو یک نوع اتاق در هر خرید امکان‌پذیر است."
                />
            </TransitionGroup>
        </template>
        <div v-else>
            <div v-show="!loadingRoom && !prices.length">
                <ReserveError
                    error="در بازه زمانی انتخابی شما، ظرفیت اتاق‌های این هتل تکمیل است!"
                    research-title="مشاهده هتل‌های مشابه"
                    wrapper-id="no-room-find"
                    @research="emit('research')"
                />
            </div>
        </div>
    </div>
</template>
<style scoped lang="scss">
.title {
    margin-bottom: 8px;
    margin-top: 0;
    @include mobile-only {
        margin-bottom: 0;
    }
}
.rooms {
    .rooms-loading {
        height: 164px;
        @include shadow();
        width: 100%;
        @include mobile-only {
            height: 214px;
        }
    }
}
</style>
